import { model } from "mobx-keystone";
// biome-ignore lint/suspicious/noShadowRestrictedNames: <it's fine probably>
import type { TypeError } from "../../utils/types";

// While this is technically Not That Great, it allows us to redefine the separator in one place if ever needed
const namespaceSeparator = ".";
export const nsS = namespaceSeparator;
type _ = typeof namespaceSeparator;

type Named<
  Application extends string,
  Store extends string,
  Model extends string | undefined,
> = undefined extends Model
  ? `${Application}${_}${Store}`
  : Model extends string
    ? `${Application}${_}${Store}${_}${Model}`
    : never;

export const createNamespace = <Application extends string>(
  app: Application,
) => {
  return <Store extends string, Model extends string | undefined>(
    store: Store,
    _model?: Model,
  ) => {
    if (_model) {
      return `${app}${nsS}${store}${nsS}${_model}` as Named<
        Application,
        Store,
        Model
      >;
    }
    return `${app}${nsS}${store}` as Named<Application, Store, Model>;
  };
};

export const NS = "tilliX";
export const namespace = createNamespace(NS);
export type KeystoneModel<
  U extends string,
  V extends string | undefined,
> = ReturnType<typeof model> & {
  __$brand: Named<"tilliX", U, V>;
};

type KeystoneModelNoIntersect<
  V extends string,
  M extends string,
  U extends string,
> = M extends V
  ? TypeError<"Base model name is reserved">
  : U extends V
    ? TypeError<"Model name already reserved">
    : KeystoneModel<`${M}Store`, V>;

type ModelNamespace<M extends string, U extends string> = {
  store: KeystoneModel<`${M}Store`, undefined>;
  model: KeystoneModel<`${M}Store`, M>;
  etc: <V extends string>(m: V) => KeystoneModelNoIntersect<V, M, U>;
} & Record<U, KeystoneModel<`${M}Store`, U>>;

/**
 * TODO: this type isn't quite right. Recursion doesn't work as expected
 *
 * ```
 * type F = ModelNameFromStore<`tilliX.invoiceStore.draftStore`> => `invoiceStore.draftStore.draft` // this is wrong
 * ```
 * so just... don't nest your store names (for now)
 */
export type ModelNameFromStore<
  S extends string,
  $Root extends string = "",
> = S extends `${infer Root}${_}${infer M}Store`
  ? ModelNameFromStore<`${M}Store`, `${Root}${_}`>
  : S extends `${infer M}Store`
    ? `${$Root}${S}${_}${M}`
    : never;
export const isStoreName = (name: string): name is `${string}Store` =>
  name.endsWith("Store");

export type StoreNameFromModel<M extends string> = `${M}Store`;

/**
 *
 * @param modelName The base model representing what is being stored
 * @param otherModels Extraneous models that you want to define imperatively. This is useful if you wish to reserve a model name for a given namespace and don't want others to use it or accidentally define it via `etc()` calls. Regardless, you probably don't need this (it's a functionality that is carried over from Beacon) and should use the `etc` method on the object that is returned from this function instead.
 *
 * @example
 * ```ts
 * // Define a namespace for invoices.
 * const invoiceNS = nsModel("invoice")
 *
 * // Equivalent to: ＠model("tilliX.invoiceStore.invoice")
 * ＠invoiceNS.model
 * export class InvoiceModel extends ExtendedModel(TModel, {}) {}
 *
 * // Equivalent to: ＠model("tilliX.invoiceStore")
 * ＠invoiceNS.store
 * export class InvoiceStore extends ExtendedModel(TStore, {}) {}
 *
 * // Equivalent to: ＠model("tilliX.invoiceStore.drafts")
 * ＠invoiceNS.etc("drafts")
 * export class DraftInvoiceModel extends ExtendedModel(TModel, {}) {}
 * ```
 *
 * ## WARNING:
 * Don't copy and paste the example code. We use a fake "@" symbol to trick the linter into thinking this is a decorator and not JSDoc syntax.
 */
export const nsModel = <M extends string, U extends string>(
  modelName: M,
  ...otherModels: U[]
): ModelNamespace<M, U> => {
  if (modelName.includes(nsS)) {
    throw new Error(
      `The model name ${modelName} cannot contain the namespace separator "${nsS}"`,
    );
  }

  const storeName = namespace(`${modelName}Store`);
  const store = model(storeName) as KeystoneModel<`${M}Store`, undefined>;
  store.__$brand = storeName;

  const _modelName = namespace(`${modelName}Store`, modelName);
  const _model = model(_modelName) as KeystoneModel<`${M}Store`, M>;
  _model.__$brand = _modelName;

  const etc = <V extends string>(m: V): KeystoneModelNoIntersect<V, M, U> => {
    if (m.includes(nsS)) {
      throw new Error(
        `The model name ${m} cannot contain the namespace separator "${nsS}"`,
      );
    }
    if ((otherModels as string[]).includes(m)) {
      throw new Error(
        `The model name ${m} is already reserved for this namespace`,
      );
    } else if ((modelName as string) === m) {
      throw new Error(
        `The model name ${m} is already reserved for the base model`,
      );
    } else if ((storeName as string) === m) {
      throw new Error(
        `The model name ${m} is already reserved for the store model`,
      );
    }

    const __modelName = namespace(`${modelName}Store`, m);
    const __model = model(__modelName) as KeystoneModel<`${M}Store`, V>;
    __model.__$brand = __modelName;
    return __model as KeystoneModelNoIntersect<V, M, U>;
  };

  const _otherModels = Object.fromEntries(
    otherModels.map((m) => {
      if (m.includes(nsS)) {
        throw new Error(
          `The model name ${m} cannot contain the namespace separator "${nsS}"`,
        );
      }
      const __modelName = namespace(`${modelName}Store`, m);
      const __model = model(__modelName) as KeystoneModel<`${M}Store`, U>;
      __model.__$brand = __modelName;
      return [m, __model];
    }),
  ) as Record<U, KeystoneModel<`${M}Store`, U>>;

  return {
    store,
    model: _model,
    etc,
    ..._otherModels,
  };
};
